import { Link, NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { api, API_BASE, getViewAs, getAdminToken, startViewAs } from '@/lib/api';
import Select from '@/components/ui/Select';
import { LayoutGrid, Send, LogOut, MessageSquare, Clapperboard, ListChecks, FileText, CalendarClock, FolderLock, Palette, Paintbrush } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import ViewAsBanner from '@/components/shell/ViewAsBanner';
import { cn } from '@/lib/cn';
import { myRequests } from '@/lib/requests';

/** Minimal shell for client-portal accounts: their work, their requests,
 *  sign out. No staff navigation, no internal surfaces. */
export default function PortalShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const uploadsOnlyLogin = user?.portal_scope === 'uploads';
  // A doctor at more than one clinic has one login; the portal shows one clinic at a time.
  const clinics = useQuery({
    queryKey: ['portal-clinics'],
    queryFn: async () => (await api.get<{ id: string; name: string; active: boolean }[]>('/portal/clinics')).data,
    enabled: user?.role === 'client',
  });
  const active = clinics.data?.find((c) => c.active);
  async function switchTo(id: string) {
    if (!id || id === active?.id) return;
    const viewAs = getViewAs();
    try {
      if (viewAs) {
        // An admin looking through her eyes: a fresh view-as token for that clinic,
        // asked for with the admin's own parked token. Her account is not changed.
        const mine = getAdminToken();
        if (!mine) { toast.error('Exit the view and start it again to look at another clinic'); return; }
        const r = await axios.post<{ access_token: string; expires_in: number }>(`${API_BASE}/admin/users/${viewAs.id}/impersonate`,
          { client_id: id }, { headers: { Authorization: `Bearer ${mine}` }, timeout: 20_000 });
        startViewAs(r.data.access_token, { ...viewAs, started_at: Date.now(), expires_at: Date.now() + r.data.expires_in * 1000 });
      } else {
        await api.post('/portal/clinic', { client_id: id });
      }
      window.location.assign(uploadsOnlyLogin ? '/portal/material' : '/portal');   // every page re-reads the new clinic
    } catch (e) {
      const d = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(typeof d === 'string' && d ? d : 'Could not switch clinic');
    }
  }

  // An uploads-only login sees the library and nothing else; a doctor login sees their own work.
  const uploadsOnly = user?.portal_scope === 'uploads';
  // a reply from KAF, or a request waiting on the clinic, shows on the Requests tab
  const reqs = useQuery({ queryKey: ['portal-requests'], queryFn: myRequests, enabled: !!user && !uploadsOnly, refetchInterval: 60000 });
  const reqBadge = (reqs.data ?? []).reduce((n, r) => n + (r.unread > 0 || (r.status === 'new' && r.waiting_on === 'clinic') ? 1 : 0), 0);
  const nav = uploadsOnly
    ? [{ to: '/portal/material', label: 'Raw material', icon: Clapperboard, end: false }]
    : [
      { to: '/portal', label: 'Your work', icon: LayoutGrid, end: true },
      { to: '/portal/track', label: 'Tracker', icon: ListChecks, end: false },
      { to: '/portal/scripts', label: 'Scripts', icon: FileText, end: false },
      { to: '/portal/requests', label: 'Requests', icon: Send, end: false },
      { to: '/portal/meetings', label: 'Meetings', icon: CalendarClock, end: false },
      { to: '/portal/chat', label: 'Chat', icon: MessageSquare, end: false },
      // the documents vault is the clinic's main login only, never a doctor-scoped one
      ...((user?.portal_scope ?? 'full') === 'full' ? [{ to: '/portal/documents', label: 'Documents', icon: FolderLock, end: false }] : []),
      // designs KAF PRO shared with the clinic (owner 2026-10-24): the main login only, read only
      ...((user?.portal_scope ?? 'full') === 'full' ? [{ to: '/portal/designs', label: 'Designs', icon: Palette, end: false }] : []),
      // the clinic makes its own designs from its brand kit (Aziz 2026-10-25): the main login only
      ...((user?.portal_scope ?? 'full') === 'full' ? [{ to: '/portal/brand-kit', label: 'Brand kit', icon: Paintbrush, end: false }] : []),
      ...(user?.can_upload_material ? [{ to: '/portal/material', label: 'Raw material', icon: Clapperboard, end: false }] : []),
    ];

  return (
    <div className="min-h-screen flex flex-col">
      <ViewAsBanner />
      <header className="sticky top-0 z-20 bg-bg/90 backdrop-blur border-b border-bg-card">
        {/* wider than the page: the main login's menu holds eleven entries (Brand kit, 2026-10-25) */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-4">
          <Link to={uploadsOnly ? '/portal/material' : '/portal'} className="flex items-center gap-2.5 min-w-0">
            <img src="/kaf-logo.png" alt="KAF PRO" className="h-7 w-7 rounded-full shrink-0" />
            <span className="text-sm font-semibold text-muted-strong truncate">
              <span className="text-accent-400">KAF</span> PRO &middot; Client portal
            </span>
          </Link>
          <nav className="flex items-center gap-0.5 sm:gap-1 min-w-0 overflow-x-auto">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                aria-label={n.label}
                title={n.label}
                className={({ isActive }) =>
                  cn(
                    'relative shrink-0 px-1.5 sm:px-3 py-1 sm:py-1.5 min-h-11 sm:min-h-0 rounded-lg text-xs font-medium inline-flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5 transition',
                    isActive
                      ? 'bg-accent-600/15 text-accent-400'
                      : 'text-muted hover:text-muted-strong hover:bg-bg-card'
                  )
                }
              >
                <n.icon size={14} />
                <span className="hidden sm:inline">{n.label}</span>
                <span className="sm:hidden text-[9px] leading-none">{n.label.split(' ')[0]}</span>
                {n.to === '/portal/requests' && reqBadge > 0 && (
                  <span className="absolute -top-0.5 -end-0.5 min-w-4 h-4 px-1 rounded-full bg-amber-400 text-[10px] text-black font-semibold leading-4 text-center" data-testid="requests-badge">{reqBadge}</span>
                )}
              </NavLink>
            ))}
            <button
              onClick={logout}
              title="Sign out"
              className="ml-1 shrink-0 px-2 sm:px-3 py-1.5 rounded-lg text-xs text-muted hover:text-muted-strong hover:bg-bg-card inline-flex items-center gap-1.5"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </nav>
        </div>
      </header>

      {(clinics.data?.length ?? 0) > 1 && (
        <div className="max-w-5xl w-full mx-auto px-4 md:px-6 pt-4 flex items-center gap-2" data-testid="portal-clinic-switch">
          <span className="text-xs text-muted">Clinic</span>
          <Select size="sm" className="min-w-[220px]" value={active?.id ?? ''} onChange={switchTo}
            options={(clinics.data ?? []).map((c) => ({ value: c.id, label: c.name }))} data-testid="portal-clinic-select" />
        </div>
      )}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 md:px-6 py-6">{children}</main>

      <footer className="border-t border-bg-card py-4 text-center text-[10px] text-muted uppercase tracking-widest">
        {user?.display_name} &middot; KAF PRO Media Production
      </footer>
    </div>
  );
}
